// src/components/Presentation/JoinSession.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Users, Wifi, WifiOff, Loader2, QrCode, LogIn } from 'lucide-react';
import { 
  getUserPresentations, 
  createPresentation, 
  deletePresentation,
  getSessionByCode 
} from '../../services/presentationAPI';
import audienceService from '../../services/audienceService';
import toast from 'react-hot-toast';

const JoinSession = () => {
  const { sessionCode } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const querySessionCode = searchParams.get('session') || sessionCode;
  
  const [joining, setJoining] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [participantName, setParticipantName] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('connecting');

  useEffect(() => {
    if (querySessionCode) {
      loadSessionInfo();
    }
  }, [querySessionCode]);

  const loadSessionInfo = async () => {
    try {
      const session = await getSessionByCode(querySessionCode);
      if (session) {
        setSessionData(session);
        setConnectionStatus('connected');
      } else {
        toast.error('Session not found');
        setConnectionStatus('disconnected');
      }
    } catch (error) {
      console.error('Error loading session:', error);
      toast.error('Failed to load session');
      setConnectionStatus('disconnected');
    }
  };

  const handleJoinSession = async () => {
    if (!querySessionCode) {
      toast.error('Please enter a session code');
      return;
    }

    if (!isAnonymous && !participantName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setJoining(true);
    try {
      const participantData = {
        name: isAnonymous ? `Guest ${Date.now().toString().slice(-4)}` : participantName.trim(),
        isAnonymous,
        joinedAt: new Date().toISOString(),
        sessionCode: querySessionCode,
        device: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language
        }
      };

      const participant = await audienceService.joinSession(querySessionCode, participantData);
      
      // Store participant info in localStorage for reconnection
      localStorage.setItem('presentationParticipant', JSON.stringify({
        id: participant.id,
        sessionCode: querySessionCode,
        name: participantData.name
      }));

      toast.success(`Joined as ${participantData.name}`);
      navigate(`/presentation/view/${querySessionCode}`, { 
        state: { participant } 
      });
    } catch (error) {
      console.error('Error joining session:', error);
      toast.error(error.message || 'Failed to join session');
    } finally {
      setJoining(false);
    }
  };

  const handleSessionCodeSubmit = (e) => {
    e.preventDefault();
    const code = e.target.sessionCode.value.trim().toUpperCase();
    if (code) {
      navigate(`/join/${code}`);
    }
  };

  if (!querySessionCode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <QrCode className="w-8 h-8 text-indigo-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Join Presentation</h1>
              <p className="text-gray-600">Enter the session code to participate</p>
            </div>

            <form onSubmit={handleSessionCodeSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Session Code
                </label>
                <input
                  type="text"
                  name="sessionCode"
                  placeholder="Enter 6-digit code"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-center text-lg font-mono uppercase"
                  maxLength={6}
                  pattern="[A-Za-z0-9]{6}"
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full bg-indigo-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-indigo-700 transition-colors"
              >
                Join Session
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Don't have a code? Ask your presenter for the session link or QR code.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Connection Status */}
          <div className="flex items-center justify-center mb-6">
            <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${
              connectionStatus === 'connected' ? 'bg-green-100 text-green-800' :
              connectionStatus === 'connecting' ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {connectionStatus === 'connected' ? <Wifi className="w-4 h-4" /> :
               connectionStatus === 'connecting' ? <Loader2 className="w-4 h-4 animate-spin" /> :
               <WifiOff className="w-4 h-4" />}
              <span className="capitalize">{connectionStatus}</span>
            </div>
          </div>

          {sessionData && (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-indigo-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{sessionData.title}</h1>
                <p className="text-gray-600">Session: {querySessionCode}</p>
                {sessionData.participantCount && (
                  <p className="text-sm text-gray-500 mt-1">
                    {sessionData.participantCount} participants joined
                  </p>
                )}
              </div>

              <div className="space-y-6">
                {/* Participation Mode */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    How would you like to participate?
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="participationType"
                        checked={isAnonymous}
                        onChange={() => setIsAnonymous(true)}
                        className="text-indigo-600"
                      />
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">Anonymous</div>
                        <div className="text-xs text-gray-500">Join without sharing your name</div>
                      </div>
                    </label>
                    <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="participationType"
                        checked={!isAnonymous}
                        onChange={() => setIsAnonymous(false)}
                        className="text-indigo-600"
                      />
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">With Name</div>
                        <div className="text-xs text-gray-500">Your responses will show your name</div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Name Input */}
                {!isAnonymous && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Name
                    </label>
                    <input
                      type="text"
                      value={participantName}
                      onChange={(e) => setParticipantName(e.target.value)}
                      placeholder="Enter your name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      required={!isAnonymous}
                    />
                  </div>
                )}

                <button
                  onClick={handleJoinSession}
                  disabled={joining || connectionStatus !== 'connected'}
                  className="w-full bg-indigo-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  {joining ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Joining...
                    </>
                  ) : (
                    <>
                      <LogIn className="w-4 h-4 mr-2" />
                      Join Presentation
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default JoinSession;